import React, { useState, useRef, useEffect } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const NotePadPage = () => {
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('Untitled');
  const [isModified, setIsModified] = useState(false);
  const [fontSize, setFontSize] = useState('14');
  const [fontFamily, setFontFamily] = useState('monospace');
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // History for undo/redo functionality
  const [history, setHistory] = useState(['']);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Menu visibility states
  const [activeMenu, setActiveMenu] = useState('');
  
  // Find and Replace states
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  
  // Refs
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Handle content change
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    setIsModified(true);
    
    // Add to history for undo/redo
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };
  
  // File operations
  const newFile = () => {
    if (isModified && !window.confirm('You have unsaved changes. Continue?')) {
      return;
    }
    setContent('');
    setFileName('Untitled');
    setIsModified(false);
    setHistory(['']);
    setHistoryIndex(0);
  };
  
  const openFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileOpen = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setContent(e.target.result);
        setFileName(file.name.replace(/\.[^/.]+$/, ''));
        setIsModified(false);
        setHistory([e.target.result]);
        setHistoryIndex(0);
      };
      reader.readAsText(file);
    }
  };
  
  const saveFile = () => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${fileName}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    setIsModified(false);
  };
  
  const printFile = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${fileName}</title>
          <style>
            body { font-family: ${fontFamily}; font-size: ${fontSize}px; white-space: pre-wrap; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };
  
  // Edit operations
  const cutText = async () => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const selectedText = content.substring(start, end);
      
      if (selectedText) {
        try {
          await navigator.clipboard.writeText(selectedText);
          const newContent = content.substring(0, start) + content.substring(end);
          setContent(newContent);
          setIsModified(true);
          addToHistory(newContent);
          
          // Set cursor position
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(start, start);
              textareaRef.current.focus();
            }
          }, 0);
        } catch (err) {
          // Silent clipboard write failure
          // Fallback: just delete the selected text
          const newContent = content.substring(0, start) + content.substring(end);
          setContent(newContent);
          setIsModified(true);
          addToHistory(newContent);
        }
      }
    }
  };
  
  const copyText = async () => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      const selectedText = content.substring(start, end);
      
      if (selectedText) {
        try {
          await navigator.clipboard.writeText(selectedText);
        } catch (err) {
          // Fallback for older browsers
          try {
            const textArea = document.createElement('textarea');
            textArea.value = selectedText;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
          } catch (fallbackErr) {
            // Silent fallback failure
          }
        }
      } else {
        // If no text selected, copy all content
        try {
          await navigator.clipboard.writeText(content);
        } catch (err) {
          // Silent failure for copying all text
        }
      }
    }
  };
  
  const pasteText = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (textareaRef.current) {
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const newContent = content.substring(0, start) + clipboardText + content.substring(end);
        setContent(newContent);
        setIsModified(true);
        addToHistory(newContent);
        
        // Set cursor position after pasted text
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.setSelectionRange(start + clipboardText.length, start + clipboardText.length);
            textareaRef.current.focus();
          }
        }, 0);
      }
    } catch (err) {
      // Silent clipboard read failure
    }
  };
  
  // History management
  const addToHistory = (newContent) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };
  
  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setContent(history[historyIndex - 1]);
      setIsModified(true);
    }
  };
  
  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setContent(history[historyIndex + 1]);
      setIsModified(true);
    }
  };
  
  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  // Select all text
  const selectAll = () => {
    if (textareaRef.current) {
      textareaRef.current.select();
      textareaRef.current.focus();
    }
  };
  
  // Find and Replace functionality
  const findAndReplace = () => {
    setShowFindReplace(true);
    closeMenus();
  };

  const highlightEditorRange = (start, end) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    textarea.focus();
    textarea.setSelectionRange(start, end);

    const lineHeight = parseFloat(window.getComputedStyle(textarea).lineHeight) || Number(fontSize) * 1.5;
    const lineIndex = content.substring(0, start).split('\n').length - 1;
    textarea.scrollTop = Math.max(0, (lineIndex * lineHeight) - (textarea.clientHeight / 2));
  };
  
  const performFind = () => {
    if (!findText) return;
    
    const searchText = matchCase ? findText : findText.toLowerCase();
    const contentText = matchCase ? content : content.toLowerCase();
    const index = contentText.indexOf(searchText);
    
    if (index !== -1 && textareaRef.current) {
      highlightEditorRange(index, index + findText.length);
    } else {
      alert('Text not found');
    }
  };
  
  const performReplace = () => {
    if (!findText) return;
    
    const searchText = matchCase ? findText : findText.toLowerCase();
    const contentText = matchCase ? content : content.toLowerCase();
    const index = contentText.indexOf(searchText);
    
    if (index !== -1) {
      const newContent = content.substring(0, index) + replaceText + content.substring(index + findText.length);
      setContent(newContent);
      setIsModified(true);
      addToHistory(newContent);
      setTimeout(() => highlightEditorRange(index, index + replaceText.length), 0);
    } else {
      alert('Text not found');
    }
  };
  
  const performReplaceAll = () => {
    if (!findText) return;
    
    let newContent = content;
    const searchRegex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), matchCase ? 'g' : 'gi');
    
    if (searchRegex.test(newContent)) {
      newContent = newContent.replace(searchRegex, replaceText);
      setContent(newContent);
      setIsModified(true);
      addToHistory(newContent);
    } else {
      alert('Text not found');
    }
  };
  
  // Menu handlers
  const toggleMenu = (menuName) => {
    setActiveMenu(activeMenu === menuName ? '' : menuName);
  };
  
  const closeMenus = () => {
    setActiveMenu('');
  };
  
  // Font options
  const fontFamilies = [
    'monospace', 'Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana', 'Georgia'
  ];
  
  const fontSizes = ['10', '12', '14', '16', '18', '20', '22', '24', '28', '32'];
  
  const isEditableField = (target) => {
    return target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target.isContentEditable;
  };
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      const shouldUseNativeEditableShortcut =
        (e.ctrlKey || e.metaKey) &&
        isEditableField(e.target) &&
        e.target !== textareaRef.current &&
        ['x', 'c', 'v', 'a', 'z', 'y'].includes(key);

      if (shouldUseNativeEditableShortcut) {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        switch (key) {
          case 'n':
            e.preventDefault();
            newFile();
            break;
          case 'o':
            e.preventDefault();
            openFile();
            break;
          case 's':
            e.preventDefault();
            saveFile();
            break;
          case 'p':
            e.preventDefault();
            printFile();
            break;
          case 'z':
            if (e.shiftKey) {
              e.preventDefault();
              redo();
            } else {
              e.preventDefault();
              undo();
            }
            break;
          case 'y':
            e.preventDefault();
            redo();
            break;
          case 'x':
            e.preventDefault();
            cutText();
            break;
          case 'c':
            e.preventDefault();
            copyText();
            break;
          case 'v':
            e.preventDefault();
            pasteText();
            break;
          case 'a':
            e.preventDefault();
            selectAll();
            break;
          case 'f':
            e.preventDefault();
            findAndReplace();
            break;
          case 'f11':
            e.preventDefault();
            toggleFullscreen();
            break;
          default:
            break;
        }
      }
      
      // F11 for fullscreen (without Ctrl)
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [content, historyIndex, history.length]);
  
  // Prevent browser default behavior for certain shortcuts
  useEffect(() => {
    const preventDefaults = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['n', 'o', 's', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('keydown', preventDefaults);
    return () => document.removeEventListener('keydown', preventDefaults);
  }, []);

  return (
    <>
      <SEO 
        title="Online Notepad - Free Text Editor" 
        description="Free online notepad with save, print, and text formatting features. Create, edit and save text files with our simple and clean text editor."
        keywords={["online notepad", "text editor", "free notepad", "text document", "note taking", "simple editor"]}
        canonicalUrl="https://yourjsontools.com/notepad"
      />
      
      {!isFullscreen && (
        <AdUnit
          slotName="notepad-top"
          adSlot="1234567890"
          format="horizontal"
          style={{ margin: '20px auto', width: '90%', maxWidth: '1200px' }}
        />
      )}
      
      <div className={`notepad-container ${isFullscreen ? 'fullscreen' : ''}`} onClick={closeMenus}>
        <div className="notepad-header">
          <h1>{isFullscreen ? '' : 'Online Notepad'}</h1>
          <div className="title-bar">
            <span className="file-name">{fileName}{isModified ? '*' : ''}</span>
            {isFullscreen && (
              <button onClick={toggleFullscreen} className="exit-fullscreen-btn">
                Exit Fullscreen
              </button>
            )}
          </div>
        </div>
        
        {/* Menu Bar */}
        <div className="menu-bar">
          <div className={`menu-item ${activeMenu === 'file' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMenu('file'); }}>
            File
            {activeMenu === 'file' && (
              <div className="notepad-dropdown-menu">
                <div className="menu-option" onClick={newFile}>New Document</div>
                <div className="menu-option" onClick={openFile}>Open...</div>
                <div className="menu-option" onClick={saveFile}>Save</div>
                <div className="menu-separator"></div>
                <div className="menu-option" onClick={printFile}>Print</div>
              </div>
            )}
          </div>
          
          <div className={`menu-item ${activeMenu === 'edit' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMenu('edit'); }}>
            Edit
            {activeMenu === 'edit' && (
              <div className="notepad-dropdown-menu">
                <div className="menu-option" onClick={undo} disabled={historyIndex <= 0}>Undo</div>
                <div className="menu-option" onClick={redo} disabled={historyIndex >= history.length - 1}>Redo</div>
                <div className="menu-separator"></div>
                <div className="menu-option" onClick={cutText}>Cut</div>
                <div className="menu-option" onClick={copyText}>Copy</div>
                <div className="menu-option" onClick={pasteText}>Paste</div>
                <div className="menu-separator"></div>
                <div className="menu-option" onClick={selectAll}>Select All</div>
                <div className="menu-option" onClick={findAndReplace}>Find and Replace</div>
              </div>
            )}
          </div>
          
          <div className={`menu-item ${activeMenu === 'insert' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMenu('insert'); }}>
            Insert
            {activeMenu === 'insert' && (
              <div className="notepad-dropdown-menu">
                <div className="menu-option" onClick={() => setContent(content + new Date().toLocaleString())}>Date/Time</div>
                <div className="menu-option" onClick={() => setContent(content + '\n---\n')}>Separator Line</div>
              </div>
            )}
          </div>
          
          <div className={`menu-item ${activeMenu === 'view' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMenu('view'); }}>
            View
            {activeMenu === 'view' && (
              <div className="notepad-dropdown-menu">
                <div className="menu-option" onClick={toggleFullscreen}>
                  Fullscreen
                </div>
              </div>
            )}
          </div>
          
          <div className={`menu-item ${activeMenu === 'help' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMenu('help'); }}>
            Help
            {activeMenu === 'help' && (
              <div className="notepad-dropdown-menu">
                <div className="menu-option" onClick={() => alert('Online Notepad v1.0\nA simple text editor with basic functionality.')}>About</div>
                <div className="menu-option" onClick={() => alert('Keyboard Shortcuts:\nCtrl+N: New File\nCtrl+O: Open File\nCtrl+S: Save File\nCtrl+P: Print\nCtrl+Z: Undo\nCtrl+Shift+Z: Redo\nCtrl+Y: Redo\nCtrl+X: Cut\nCtrl+C: Copy\nCtrl+V: Paste\nCtrl+A: Select All\nCtrl+F: Find and Replace\nF11: Toggle Fullscreen')}>Keyboard Shortcuts</div>
              </div>
            )}
          </div>
        </div>
        
        {/* Toolbar */}
        <div className="toolbar">
          <button onClick={newFile} title="New File" className="toolbar-btn">📄</button>
          <button onClick={openFile} title="Open File" className="toolbar-btn">📁</button>
          <button onClick={saveFile} title="Save File" className="toolbar-btn">💾</button>
          <button onClick={printFile} title="Print" className="toolbar-btn">🖨️</button>
          
          <div className="separator"></div>
          
          <button onClick={cutText} title="Cut" className="toolbar-btn">✂️</button>
          <button onClick={copyText} title="Copy" className="toolbar-btn">📋</button>
          <button onClick={pasteText} title="Paste" className="toolbar-btn">📄</button>
          
          <div className="separator"></div>
          
          <button onClick={undo} title="Undo" disabled={historyIndex <= 0} className="toolbar-btn">↶</button>
          <button onClick={redo} title="Redo" disabled={historyIndex >= history.length - 1} className="toolbar-btn">↷</button>
          
          <div className="separator"></div>
          
          <select 
            value={fontFamily} 
            onChange={(e) => setFontFamily(e.target.value)}
            className="font-selector"
          >
            {fontFamilies.map(font => (
              <option key={font} value={font}>{font}</option>
            ))}
          </select>
          
          <select 
            value={fontSize} 
            onChange={(e) => setFontSize(e.target.value)}
            className="font-size-selector"
          >
            {fontSizes.map(size => (
              <option key={size} value={size}>{size}px</option>
            ))}
          </select>
          
          <button onClick={toggleFullscreen} title="Toggle Fullscreen" className="toolbar-btn">⛶</button>
        </div>
        
        {/* Text Editor */}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          className="text-editor"
          style={{
            fontFamily: fontFamily,
            fontSize: `${fontSize}px`
          }}
          placeholder="Start typing your text here..."
        />
        
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".txt,.text"
          onChange={handleFileOpen}
        />
        
        {/* Find and Replace Modal */}
        {showFindReplace && (
          <div className="modal-overlay" onClick={() => setShowFindReplace(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Find and Replace</h3>
                <button className="modal-close" onClick={() => setShowFindReplace(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="input-group">
                  <label>Find:</label>
                  <input
                    type="text"
                    value={findText}
                    onChange={(e) => setFindText(e.target.value)}
                    placeholder="Enter text to find"
                    autoFocus
                  />
                </div>
                <div className="input-group">
                  <label>Replace:</label>
                  <input
                    type="text"
                    value={replaceText}
                    onChange={(e) => setReplaceText(e.target.value)}
                    placeholder="Enter replacement text"
                  />
                </div>
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={matchCase}
                      onChange={(e) => setMatchCase(e.target.checked)}
                    />
                    Match case
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={performFind} className="modal-btn">Find</button>
                <button onClick={performReplace} className="modal-btn">Replace</button>
                <button onClick={performReplaceAll} className="modal-btn">Replace All</button>
                <button onClick={() => setShowFindReplace(false)} className="modal-btn cancel">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style jsx="global">{`
        .notepad-container {
          display: flex;
          flex-direction: column;
          width: 90%;
          max-width: 1200px;
          margin: 0 auto;
          min-height: 600px;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .notepad-container.fullscreen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          max-width: none;
          margin: 0;
          border-radius: 0;
          z-index: 9999;
          border: none;
        }
        
        .notepad-header {
          background: #f8f9fa;
          padding: 10px 20px;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .notepad-header h1 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }
        
        .title-bar {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .file-name {
          font-weight: bold;
          color: #555;
        }
        
        .exit-fullscreen-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 12px;
        }
        
        .menu-bar {
          background: #e9ecef;
          padding: 0;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          position: relative;
        }
        
        .menu-item {
          position: relative;
          padding: 8px 15px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          border-right: 1px solid #dee2e6;
        }
        
        .menu-item:hover {
          background: #dee2e6;
        }
        
        .notepad-dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: white;
          border: 1px solid #ccc;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          min-width: 120px;
          z-index: 1000;
          display: none;
        }

        .menu-item:hover .notepad-dropdown-menu,
        .menu-item.active .notepad-dropdown-menu {
          display: block;
        }
        
        .menu-option {
          padding: 8px 12px;
          cursor: pointer;
          font-size: 13px;
          color: #333;
        }
        
        .menu-option:hover {
          background: #f8f9fa;
        }
        
        .menu-option[disabled] {
          color: #999;
          cursor: not-allowed;
        }
        
        .menu-separator {
          height: 1px;
          background: #dee2e6;
          margin: 2px 0;
        }
        
        .toolbar {
          background: #f8f9fa;
          padding: 8px;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          align-items: center;
          gap: 5px;
          flex-wrap: wrap;
        }
        
        .toolbar-btn {
          background: none;
          border: 1px solid transparent;
          padding: 6px 8px;
          cursor: pointer;
          border-radius: 3px;
          font-size: 16px;
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .toolbar-btn:hover {
          background: #e9ecef;
          border-color: #adb5bd;
        }
        
        .toolbar-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .separator {
          width: 1px;
          height: 24px;
          background: #dee2e6;
          margin: 0 5px;
        }
        
        .font-selector, .font-size-selector {
          padding: 4px 6px;
          border: 1px solid #ced4da;
          border-radius: 3px;
          font-size: 12px;
        }
        
        .font-selector {
          min-width: 100px;
        }
        
        .font-size-selector {
          min-width: 60px;
        }
        
        .text-editor {
          flex: 1;
          width: 100%;
          border: none;
          outline: none;
          padding: 15px;
          resize: none;
          line-height: 1.5;
          background: #fff;
        }

        .text-editor::selection {
          background: #ffeb3b;
          color: #111;
        }

        .text-editor::-moz-selection {
          background: #ffeb3b;
          color: #111;
        }
        
        .notepad-container.fullscreen .text-editor {
          height: calc(100vh - 120px);
        }
        
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }
        
        .modal-content {
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          width: 400px;
          max-width: 90vw;
        }
        
        .modal-header {
          padding: 15px 20px;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .modal-header h3 {
          margin: 0;
          font-size: 16px;
          color: #333;
        }
        
        .modal-close {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .modal-close:hover {
          background: #f8f9fa;
          border-radius: 4px;
        }
        
        .modal-body {
          padding: 20px;
        }
        
        .input-group {
          margin-bottom: 15px;
        }
        
        .input-group label {
          display: block;
          margin-bottom: 5px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }
        
        .input-group input[type="text"] {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
        }
        
        .input-group input[type="text"]:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }
        
        .checkbox-group {
          margin-top: 15px;
        }
        
        .checkbox-group label {
          display: flex;
          align-items: center;
          font-size: 14px;
          cursor: pointer;
        }
        
        .checkbox-group input[type="checkbox"] {
          margin-right: 8px;
        }
        
        .modal-footer {
          padding: 15px 20px;
          border-top: 1px solid #dee2e6;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }
        
        .modal-btn {
          padding: 8px 16px;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          background: #f8f9fa;
          color: #333;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .modal-btn:hover {
          background: #e9ecef;
          border-color: #adb5bd;
        }
        
        .modal-btn.cancel {
          background: #6c757d;
          color: white;
          border-color: #6c757d;
        }
        
        .modal-btn.cancel:hover {
          background: #5a6268;
          border-color: #545b62;
        }
        
        @media (max-width: 768px) {
          .notepad-header {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
          }
          
          .toolbar {
            flex-wrap: wrap;
            gap: 3px;
          }
          
          .toolbar-btn {
            min-width: 28px;
            height: 28px;
            font-size: 14px;
          }
          
          .font-selector, .font-size-selector {
            font-size: 11px;
          }
          
          .menu-bar {
            flex-wrap: wrap;
          }
          
          .menu-item {
            padding: 6px 10px;
            font-size: 13px;
          }
        }
      `}</style>
      
      {!isFullscreen && (
        <>
          <div style={{ width: '90%', maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
            <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About Online Notepad</h2>
            <p style={{ marginBottom: '15px' }}>
              This is a simple, clean online notepad that works in your browser. Create, edit, and save text files without any installation required.
            </p>
            
            <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Features:</h3>
            <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
              <li style={{ marginBottom: '8px' }}><strong>File Operations:</strong> Create new files, open existing ones, save your work, and print documents.</li>
              <li style={{ marginBottom: '8px' }}><strong>Text Editing:</strong> Cut, copy, paste text with full undo/redo support.</li>
              <li style={{ marginBottom: '8px' }}><strong>Formatting:</strong> Choose from various fonts and font sizes for better readability.</li>
              <li style={{ marginBottom: '8px' }}><strong>Fullscreen Mode:</strong> Focus on your writing with distraction-free fullscreen editing.</li>
              <li style={{ marginBottom: '8px' }}><strong>Keyboard Shortcuts:</strong> Familiar shortcuts for power users (Ctrl+S, Ctrl+Z, etc.)</li>
            </ul>
            
            <p style={{ marginBottom: '15px' }}>
              All your text is processed locally in your browser - no data is sent to our servers, ensuring your privacy and security.
            </p>
          </div>
          
        </>
      )}
    </>
  );
};

export default NotePadPage;